/**
 * Career DNA™ destination surface — Phase 3 of the V2 redesign.
 *
 * Presentation only. Every value, sentence and label here comes verbatim
 * from the sealed Experience Engine™ payload (`learner_reports`). Nothing
 * is computed, ranked or re-worded client-side; the only local content is
 * the learner-voice headline per intelligence family, which mirrors the
 * engine's student lexicon.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import DnaFramework from "./DnaFramework";
import { bar, pct, type FeatureRow, type IntelRow, type StudentPayload } from "./reportTypes";

/** Learner-voice headline per intelligence family (mirrors the engine's student lexicon). */
const HEADLINES: Record<string, string> = {
  purpose: "You're clear about why the work matters.",
  strength: "One strength keeps showing up in your answers.",
  interest: "Your curiosity has a real theme running through it.",
  learning: "You have a distinctive learning rhythm — lean into it.",
  capability: "You're already ready to take on more here.",
  growth: "This is where your biggest next step lives.",
};

function headlineFor(code: string): string {
  const key = Object.keys(HEADLINES).find((k) => code.toLowerCase().includes(k));
  return key ? HEADLINES[key] : "This one shapes how you work.";
}

/** One Wrapped-style card: meaning first, magnitude second. */
function DnaCard({ row, index }: { row: IntelRow; index: number }) {
  const accents = [
    "from-primary/25 via-primary/5",
    "from-[hsl(var(--ai))]/25 via-[hsl(var(--ai))]/5",
    "from-[hsl(var(--celebrate))]/25 via-[hsl(var(--celebrate))]/5",
  ];
  const accent = accents[index % accents.length];

  return (
    <article
      className={`calm-card relative overflow-hidden bg-gradient-to-br ${accent} to-transparent p-6`}
    >
      <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {row.intelligence_name}
      </p>
      <h3 className="mt-3 font-display text-xl font-semibold leading-snug text-foreground">
        {headlineFor(row.intelligence_code)}
      </h3>
      {row.explainability?.explanation && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {row.explainability.explanation}
        </p>
      )}
      <div className="mt-6 flex items-center gap-3">
        <div
          className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted/50"
          role="img"
          aria-label={`${row.intelligence_name}: ${pct(row.value)} of 100`}
        >
          <div
            className="h-full rounded-full bg-foreground/70"
            style={{ width: `${bar(row.value)}%` }}
          />
        </div>
        <span className="shrink-0 font-display text-sm tabular-nums text-foreground/80">
          {pct(row.value)}
        </span>
      </div>
    </article>
  );
}

function FeatureChips({ label, rows }: { label: string; rows: FeatureRow[] }) {
  if (rows.length === 0) return null;
  return (
    <section className="calm-card-quiet p-5">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </h3>
      <ul className="mt-3 flex flex-wrap gap-2">
        {rows.slice(0, 8).map((f) => (
          <li
            key={f.feature_key}
            className="min-h-9 rounded-2xl border border-border/60 px-3 py-2 text-sm text-foreground/85"
          >
            {f.feature_label}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function DnaSurface({ payload }: { payload: StudentPayload }) {
  const top = payload.career_dna.top_intelligences;
  const name = payload.learner.display_name?.split(" ")[0];
  const topMatch = payload.recommendations[0];

  return (
    <div className="space-y-5">
      <header className="calm-card p-6">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-primary/12 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-primary">
          <Sparkles className="h-3 w-3" aria-hidden="true" /> Career DNA™
        </p>
        <h2 className="mt-4 font-display text-2xl font-bold leading-tight sm:text-3xl">
          {name ? `${name}, this is how you work.` : "This is how you work."}
        </h2>
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground">
          Six signals, drawn straight from your answers. Meaning first — the number
          is only there to show how strongly it came through.
        </p>
        {topMatch && (
          <Link
            to="/report#matches"
            className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-2xl bg-primary px-4 text-sm font-medium text-primary-foreground transition-transform hover:scale-[1.02] active:scale-95"
          >
            See why {topMatch.career_name} fits
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}
      </header>

      {top.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {top.map((row, i) => (
            <DnaCard key={row.intelligence_code} row={row} index={i} />
          ))}
        </div>
      ) : (
        <p className="calm-card-quiet p-6 text-sm text-muted-foreground">
          Your Career DNA™ is still being written — check back shortly.
        </p>
      )}

      <DnaFramework rows={payload.career_dna.all} />

      <div className="grid gap-4 sm:grid-cols-2">
        <FeatureChips label="Where you'll grow next" rows={payload.growth_opportunities} />
        <FeatureChips label="Skills that carry you forward" rows={payload.future_skills} />
      </div>
    </div>
  );
}
